import { get as apiGet, post as apiPost, put as apiPut, postFormData as apiPostFormData } from './apiService';
import { getActiveProfile, setActiveProfile, setActiveTeam, getActiveTeam } from './profileService';
import { Profile } from '../types/profile';
import { ProfileSummary } from '../types/profile-summary';
import { Team } from '../types/team';
import { TeamSummary } from '../types/teamSummary';

// Test mode for local development
// used in getTeamSummary()
// set to 1 or 2 for # rows of test data in your widgets
let testMode = 0;

// Setter for test mode
export function setTestMode(value: number) {
  testMode = value;
}

interface CreateTeamResponse {
  business: Team;
  profile: Profile;
}

// Create a new team/business
export async function createTeam(businessName: string): Promise<CreateTeamResponse> {
  const profile = getActiveProfile();
  if (!profile) {
    throw new Error('No active profile');
  }

  const response = await apiPost<CreateTeamResponse>('/create-business', {
    name: businessName,
    email: profile.email,
    userId: profile.userId,
  });

  setActiveProfile(response.profile);
  setActiveTeam(response.business);

  return response;
}

// Get team by ID
export async function getTeam(): Promise<Team> {
  const team = getActiveTeam();
  if (!team) {
    throw new Error('No active team');
  }

  const response = await apiGet<Team>(`/business/${team.id}?expand=activePlan`);
  setActiveTeam(response);

  return response;
}

export async function getTeamById(teamId: string, serviceKey?: string): Promise<Team> {
  const response = await apiGet<Team>(`/business/${teamId}?expand=activePlan,profiles`, serviceKey);
  return response;
}

// Load team from profile's businessId (handles page refresh)
export async function loadTeamFromProfile(): Promise<Team | null> {
  // If team already loaded in store, return it
  const existingTeam = getActiveTeam();
  if (existingTeam) {
    return existingTeam;
  }

  // Check if profile has a businessId
  const profile = getActiveProfile();
  if (!profile?.businessId) {
    // If profile has no businessId, set activeTeam to null
    setActiveTeam(null);
    return null;
  }

  // Fetch team from backend using profile's businessId
  const response = await apiGet<Team>(`/business/${profile.businessId}?expand=activePlan`);
  setActiveTeam(response);

  return response;
}

// Update team
export async function updateTeam(team: Team): Promise<Team> {
  return apiPut<Team>(`/business/${team.id}`, team);
}

// Upload team avatar
export async function uploadTeamAvatar(file: File): Promise<Team> {
  const team = getActiveTeam();
  if (!team) {
    throw new Error('No active team');
  }

  const formData = new FormData();
  formData.append('file', file);

  return apiPostFormData<Team>(`/business/${team.id}/avatar`, formData);
}

// Upload team logo
export async function uploadTeamLogo(file: File): Promise<Team> {
  const team = getActiveTeam();
  if (!team) {
    throw new Error('No active team');
  }

  const formData = new FormData();
  formData.append('file', file);

  return apiPostFormData<Team>(`/business/${team.id}/logo`, formData);
}

// Get team summary with users
export async function getTeamSummary(): Promise<TeamSummary> {
  const team = getActiveTeam();
  if (!team) {
    return new TeamSummary();
  }

  let endpoint = `/business/${team.id}/summary`;

  const isLocal = !import.meta.env.VITE_ENV?.startsWith('prod');
  if (isLocal && testMode) endpoint += `?test=${testMode}`;

  return apiGet<TeamSummary>(endpoint);
}

// Suspend user
export async function suspendUser(profileId: string): Promise<Profile> {
  const team = getActiveTeam();
  if (!team) {
    throw new Error('No active team');
  }

  return apiPut<Profile>(`/business/${team.id}/profile/${profileId}/suspend`, {});
}

// Activate user
export async function activateUser(profileId: string): Promise<Profile> {
  const team = getActiveTeam();
  if (!team) {
    throw new Error('No active team');
  }

  return apiPut<Profile>(`/business/${team.id}/profile/${profileId}/activate`, {});
}

// Remove user
export async function removeUser(profileId: string): Promise<Profile> {
  const team = getActiveTeam();
  if (!team) {
    throw new Error('No active team');
  }

  return apiPut<Profile>(`/business/${team.id}/profile/${profileId}/remove`, {});
}

// Get removed users
export async function getRemovedUsers(): Promise<ProfileSummary[]> {
  const team = getActiveTeam();
  if (!team) {
    return [];
  }

  return apiGet<ProfileSummary[]>(`/business/${team.id}/removed-profiles`);
}

// Convenience export
export const teamService = {
  createTeam,
  getTeam,
  getTeamById,
  loadTeamFromProfile,
  updateTeam,
  uploadTeamAvatar,
  uploadTeamLogo,
  getTeamSummary,
  suspendUser,
  activateUser,
  removeUser,
  getRemovedUsers,
};
